import type { ReactElement } from 'react';

export type CompareVisualType = 'layout' | 'size' | 'share' | 'content' | 'revision' | 'collab';

type CompareVisualProps = {
  type: CompareVisualType;
  side: 'before' | 'after';
};

type VisualComponent = () => ReactElement;

const muted = '#7a8480';
const line = '#aeb7b1';
const dim = '#4f5854';
const accent = '#de7650';
const accentSoft = '#eb8660';
const surface = '#1a201e';
const surfaceAlt = '#232926';
const ink = '#d8ddda';

function LayoutBefore() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surface} />
      <rect x="24" y="24" width="312" height="152" rx="8" stroke={dim} strokeWidth="1.5" strokeDasharray="6 4" />
      <rect x="72" y="56" width="216" height="120" rx="4" stroke={muted} strokeWidth="2" />
      <rect x="108" y="92" width="48" height="32" rx="2" fill={dim} opacity=".55" />
      <rect x="168" y="88" width="56" height="40" rx="2" fill={dim} opacity=".45" />
      <rect x="236" y="96" width="36" height="28" rx="2" fill={dim} opacity=".4" />
      <path d="M180 118h0" stroke={line} strokeWidth="2" strokeLinecap="round" />
      <circle cx="180" cy="118" r="22" stroke={muted} strokeWidth="2" strokeDasharray="4 4" />
      <text x="180" y="125" textAnchor="middle" fill={muted} fontSize="22" fontWeight="700" fontFamily="system-ui, sans-serif">?</text>
      <text x="180" y="178" textAnchor="middle" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">2D 도면만으로는 높이·시선 파악 불가</text>
    </svg>
  );
}

function LayoutAfter() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surfaceAlt} />
      <path d="M72 148 180 88 288 148V168H72V148Z" fill="#2a3330" stroke={accent} strokeWidth="1.5" />
      <path d="M72 148 180 88 288 148" stroke={accentSoft} strokeWidth="2" />
      <rect x="128" y="118" width="64" height="30" rx="3" fill="#36403c" stroke={line} strokeWidth="1" />
      <rect x="200" y="112" width="40" height="36" rx="3" fill="#36403c" stroke={line} strokeWidth="1" />
      <circle cx="248" cy="100" r="14" fill={accent} opacity=".18" stroke={accent} strokeWidth="1.5" />
      <path d="M244 100h8M248 96v8" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M248 86c-8 0-14 6-14 14 0 10 14 22 14 22s14-12 14-22c0-8-6-14-14-14Z" stroke={accent} strokeWidth="1.5" fill="none" />
      <text x="180" y="178" textAnchor="middle" fill={ink} fontSize="11" fontFamily="system-ui, sans-serif">3D에서 동선·시선을 함께 확인</text>
    </svg>
  );
}

function SizeBefore() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surface} />
      <rect x="88" y="36" width="184" height="112" rx="8" fill="#222826" stroke={dim} strokeWidth="1.5" />
      <rect x="108" y="56" width="144" height="72" rx="4" fill={dim} opacity=".35" />
      <text x="180" y="88" textAnchor="middle" fill={muted} fontSize="13" fontFamily="system-ui, sans-serif">카탈로그</text>
      <text x="180" y="108" textAnchor="middle" fill={line} fontSize="15" fontWeight="600" fontFamily="system-ui, sans-serif" opacity=".7">약 W1200 × D600</text>
      <text x="180" y="128" textAnchor="middle" fill={muted} fontSize="10" fontFamily="system-ui, sans-serif">(실측 불명)</text>
      <path d="M60 160h240" stroke={muted} strokeWidth="1.5" strokeDasharray="5 4" />
      <text x="180" y="178" textAnchor="middle" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">대략적 치수로 현장 오차 발생</text>
    </svg>
  );
}

function SizeAfter() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surfaceAlt} />
      <rect x="118" y="72" width="124" height="64" rx="6" fill="#36403c" stroke={accent} strokeWidth="1.5" />
      <path d="M118 148h124" stroke={accent} strokeWidth="1.5" />
      <path d="M118 148v-8M242 148v-8" stroke={accent} strokeWidth="1.5" />
      <text x="180" y="164" textAnchor="middle" fill={accentSoft} fontSize="11" fontWeight="700" fontFamily="system-ui, sans-serif">1240 mm</text>
      <path d="M106 72v64" stroke={accent} strokeWidth="1.5" />
      <path d="M106 72h-8M106 136h-8" stroke={accent} strokeWidth="1.5" />
      <text x="88" y="108" textAnchor="middle" fill={accentSoft} fontSize="11" fontWeight="700" fontFamily="system-ui, sans-serif" transform="rotate(-90 88 108)">640 mm</text>
      <text x="180" y="178" textAnchor="middle" fill={ink} fontSize="11" fontFamily="system-ui, sans-serif">mm 단위 실측 배치</text>
    </svg>
  );
}

function ShareBefore() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surface} />
      <rect x="52" y="48" width="72" height="88" rx="6" fill="#252b28" stroke={dim} strokeWidth="1.5" transform="rotate(-8 88 92)" />
      <text x="76" y="98" fill={muted} fontSize="11" fontWeight="700" fontFamily="system-ui, sans-serif" transform="rotate(-8 88 92)">PPT</text>
      <rect x="144" y="44" width="72" height="88" rx="6" fill="#252b28" stroke={dim} strokeWidth="1.5" />
      <text x="168" y="94" fill={muted} fontSize="11" fontWeight="700" fontFamily="system-ui, sans-serif">PNG</text>
      <rect x="236" y="52" width="72" height="88" rx="6" fill="#252b28" stroke={dim} strokeWidth="1.5" transform="rotate(7 272 96)" />
      <text x="260" y="98" fill={muted} fontSize="11" fontWeight="700" fontFamily="system-ui, sans-serif" transform="rotate(7 272 96)">PDF</text>
      <path d="M88 148c24-12 48-8 72 0s48 12 72 0" stroke={muted} strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
      <text x="180" y="178" textAnchor="middle" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">파일·캡처를 여러 번 전달</text>
    </svg>
  );
}

function ShareAfter() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surfaceAlt} />
      <rect x="48" y="40" width="264" height="104" rx="10" fill="#1d2220" stroke={accent} strokeWidth="1.5" />
      <circle cx="68" cy="58" r="4" fill="#e05a4a" />
      <circle cx="82" cy="58" r="4" fill="#d4a017" />
      <circle cx="96" cy="58" r="4" fill="#5cb85c" />
      <rect x="64" y="72" width="232" height="28" rx="6" fill="#141816" stroke={dim} strokeWidth="1" />
      <path d="M76 86h8v-6a6 6 0 0 1 12 0v6h8" stroke={accent} strokeWidth="1.5" fill="none" />
      <text x="108" y="90" fill={ink} fontSize="10" fontFamily="ui-monospace, monospace">expospace.app/share/abc</text>
      <rect x="64" y="108" width="232" height="24" rx="6" fill={accent} opacity=".22" />
      <text x="180" y="124" textAnchor="middle" fill={accentSoft} fontSize="10" fontWeight="700" fontFamily="system-ui, sans-serif">링크 복사 · 읽기 전용 공유</text>
      <text x="180" y="178" textAnchor="middle" fill={ink} fontSize="11" fontFamily="system-ui, sans-serif">한 링크로 검토 요청</text>
    </svg>
  );
}

function ContentBefore() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surface} />
      <rect x="56" y="52" width="96" height="96" rx="4" stroke={dim} strokeWidth="2" strokeDasharray="5 4" />
      <text x="104" y="106" textAnchor="middle" fill={muted} fontSize="20" fontFamily="system-ui, sans-serif">?</text>
      <rect x="208" y="68" width="96" height="56" rx="4" fill="#252b28" stroke={dim} strokeWidth="1.5" />
      <path d="M216 116h80v12H208l8-12Z" fill={dim} />
      <rect x="228" y="128" width="56" height="8" rx="2" fill={dim} />
      <text x="180" y="178" textAnchor="middle" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">설치 후에야 실제 크기 확인</text>
    </svg>
  );
}

function ContentAfter() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surfaceAlt} />
      <rect x="56" y="52" width="96" height="96" rx="4" fill="url(#bannerGrad)" stroke={accent} strokeWidth="1.5" />
      <defs>
        <linearGradient id="bannerGrad" x1="56" y1="52" x2="152" y2="148" gradientUnits="userSpaceOnUse">
          <stop stopColor="#de7650" stopOpacity=".45" />
          <stop offset="1" stopColor="#36403c" />
        </linearGradient>
      </defs>
      <text x="104" y="106" textAnchor="middle" fill={ink} fontSize="9" fontWeight="700" fontFamily="system-ui, sans-serif">BANNER</text>
      <rect x="208" y="68" width="96" height="56" rx="4" fill="#141816" stroke={accent} strokeWidth="1.5" />
      <path d="M248 88l16 10-16 10V88Z" fill={accent} />
      <path d="M216 116h80v12H208l8-12Z" fill="#36403c" />
      <rect x="228" y="128" width="56" height="8" rx="2" fill="#36403c" />
      <text x="180" y="178" textAnchor="middle" fill={ink} fontSize="11" fontFamily="system-ui, sans-serif">배너·TV를 배치 전에 연결</text>
    </svg>
  );
}

function RevisionBefore() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surface} />
      <path d="M120 148V88" stroke={muted} strokeWidth="3" />
      <path d="M112 96h16" stroke={muted} strokeWidth="3" />
      <rect x="168" y="100" width="80" height="48" rx="3" fill="#2a3330" stroke={dim} strokeWidth="1.5" transform="rotate(-6 208 124)" />
      <circle cx="248" cy="132" r="10" fill={dim} />
      <path d="M244 132h8M248 128v8" stroke={line} strokeWidth="1.5" />
      <path d="M248 118v-14h12" stroke={muted} strokeWidth="2" strokeLinecap="round" />
      <text x="180" y="178" textAnchor="middle" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">현장 재설치 · 일정·비용 증가</text>
    </svg>
  );
}

function RevisionAfter() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surfaceAlt} />
      <rect x="56" y="36" width="248" height="120" rx="10" fill="#1d2220" stroke={accent} strokeWidth="1.5" />
      <rect x="72" y="52" width="216" height="88" rx="6" fill="#141816" />
      <path d="M96 120 140 96 184 108 228 84" stroke={accentSoft} strokeWidth="2" fill="rgba(222,118,80,.12)" />
      <rect x="168" y="92" width="40" height="28" rx="3" fill="#36403c" stroke={accent} strokeWidth="1.5" />
      <path d="M228 84l8-8 8 8-8 8-8-8Z" fill={accent} />
      <circle cx="228" cy="84" r="12" stroke={accent} strokeWidth="1" fill="none" opacity=".4" />
      <text x="180" y="178" textAnchor="middle" fill={ink} fontSize="11" fontFamily="system-ui, sans-serif">브라우저에서 즉시 수정</text>
    </svg>
  );
}

function CollabBefore() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surface} />
      <path d="M64 72h48v36H64V72Z" stroke={dim} strokeWidth="1.5" fill="#252b28" />
      <path d="M156 56h48v36h-48V56Z" stroke={dim} strokeWidth="1.5" fill="#252b28" />
      <path d="M248 80h48v36h-48V80Z" stroke={dim} strokeWidth="1.5" fill="#252b28" />
      <path d="M88 108v24M180 92v40M272 116v16" stroke={muted} strokeWidth="1.5" strokeDasharray="4 4" />
      <text x="88" y="92" textAnchor="middle" fill={muted} fontSize="9" fontFamily="system-ui, sans-serif">v2</text>
      <text x="180" y="76" textAnchor="middle" fill={muted} fontSize="9" fontFamily="system-ui, sans-serif">final</text>
      <text x="272" y="100" textAnchor="middle" fill={muted} fontSize="9" fontFamily="system-ui, sans-serif">old</text>
      <text x="180" y="178" textAnchor="middle" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">버전·파일이 흩어짐</text>
    </svg>
  );
}

function CollabAfter() {
  return (
    <svg viewBox="0 0 360 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="360" height="200" fill={surfaceAlt} />
      <circle cx="180" cy="96" r="28" fill={accent} opacity=".16" stroke={accent} strokeWidth="1.5" />
      <path d="M180 78v12M180 102v12M162 96h-12M192 96h12" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="180" cy="96" r="8" fill={accent} opacity=".35" />
      <circle cx="88" cy="72" r="14" fill="#36403c" stroke={line} strokeWidth="1.5" />
      <circle cx="272" cy="72" r="14" fill="#36403c" stroke={line} strokeWidth="1.5" />
      <circle cx="88" cy="132" r="14" fill="#36403c" stroke={line} strokeWidth="1.5" />
      <circle cx="272" cy="132" r="14" fill="#36403c" stroke={line} strokeWidth="1.5" />
      <path d="M102 78 158 90M258 78 202 90M102 126 158 108M258 126 202 108" stroke={accentSoft} strokeWidth="1.5" />
      <text x="180" y="178" textAnchor="middle" fill={ink} fontSize="11" fontFamily="system-ui, sans-serif">클라우드 프로젝트로 팀 협업</text>
    </svg>
  );
}

const visuals: Record<CompareVisualType, { before: VisualComponent; after: VisualComponent }> = {
  layout: { before: LayoutBefore, after: LayoutAfter },
  size: { before: SizeBefore, after: SizeAfter },
  share: { before: ShareBefore, after: ShareAfter },
  content: { before: ContentBefore, after: ContentAfter },
  revision: { before: RevisionBefore, after: RevisionAfter },
  collab: { before: CollabBefore, after: CollabAfter },
};

export function CompareVisual({ type, side }: CompareVisualProps) {
  const Visual = visuals[type][side];
  return <Visual />;
}
